import moment from "moment";
import React from "react";
import { Loader, Message } from "semantic-ui-react";
import FlightSearchResult from "./flightsearchresult";
import SearchFlight from "./searchflight";

interface Props {
  onManualEntryClicked: () => void
  onSelectWithSegmentClicked: (segments: server.dto.FlightSegment[]) => void

  initialDate?: Date
}

interface State {
  isFetching: boolean
  results: server.dto.FlightSegment[]
  isError: boolean
  selectedSegmentIds: Record<number, boolean>

  airlineLeftEmptyError: boolean
  flightNumberLeftEmptyError: boolean
  dateBeforeTodayError: boolean
}
// Used to assign unique and semi-stable keys to found flight results
let keyCounter = 0;

export default class RetrieveFlights extends React.Component<Props, State> {
  state: State = {
    isFetching: false,
    isError: false,
    results: null,
    selectedSegmentIds: {},
    airlineLeftEmptyError: false,
    flightNumberLeftEmptyError: false,
    dateBeforeTodayError: false
  };

  onFlightSearch = async (airline: server.dto.Airline, flightNumber: string, departure: moment.Moment) => {
    const airlineEmpty = airline == null;
    const flightNumberEmpty = flightNumber.trim() === "";
    const dateBeforeToday = departure.isBefore(moment.utc().startOf("day"));

    this.setState({
      airlineLeftEmptyError: airlineEmpty,
      flightNumberLeftEmptyError: flightNumberEmpty,
      dateBeforeTodayError: dateBeforeToday,
      isError: false
    });

    if (airlineEmpty || flightNumberEmpty || dateBeforeToday) {
      return;
    }

    this.setState({ isFetching: true, isError: false, results: null, selectedSegmentIds: {} });

    try {
      const flightNr = parseInt(flightNumber, 10);
      const result = await window.Api.Flights.RetrieveFlightDetails(flightNr, airline.airlineId, departure.toDate());
      const checkedSegments: Record<number, boolean> = {};
      result.data.forEach((r, index) => {
        r.id = ++keyCounter;
        checkedSegments[r.id] = index === 0;
      });

      this.setState({ results: result.data, selectedSegmentIds: checkedSegments });
    } catch {
      this.setState({ isError: true, results: null });
    } finally {
      this.setState({ isFetching: false });
    }
  };

  onSegmentClicked = (segment: server.dto.FlightSegment) => {
    const isAlreadyChecked = this.state.selectedSegmentIds[segment.id];

    const newSelected = { ...this.state.selectedSegmentIds, [segment.id]: !isAlreadyChecked };
    this.setState({ selectedSegmentIds: newSelected });
  };

  onCreateWithSelectedSegments = () => {
    const selectedSegments = this.state.results
      .filter(res => this.state.selectedSegmentIds[res.id]);

    this.props.onSelectWithSegmentClicked(selectedSegments);
  };

  public render(): JSX.Element {
    const { isFetching, isError, results, selectedSegmentIds, dateBeforeTodayError } = this.state;

    const noResults = results != null && results.length === 0 && !isFetching && !isError;
    const hasServerError = !isFetching && isError;
    const showResults = results != null && results.length > 0;
    const nothingSelected = Object.values(selectedSegmentIds).every(val => !val) || Object.values(selectedSegmentIds).length === 0;

    let errorMessage = "";
    if (dateBeforeTodayError) {
      errorMessage = RESX.Flight.FlightsInThePastCannot;
    } else if (hasServerError) {
      errorMessage = RESX.GeneralLabels.SomethingUnexpectedHappened;
    } else if (noResults) {
      errorMessage = RESX.Flight.NoMatchingFlightsFound;
    }
    const hasError = errorMessage !== "";

    return <>
      <div className="padding-large panel-header-color margin-bottom-larger flex radius-big align-center">
        <SearchFlight
          onSearch={this.onFlightSearch}
          airlineHasError={this.state.airlineLeftEmptyError}
          flightNumberHasError={this.state.flightNumberLeftEmptyError}
          initialDate={this.props.initialDate}
        />
      </div>
      <div className="grow1 scrollbox-auto">
        {isFetching && <Loader active inline="centered" />}

        {hasError && <Message color="red">{errorMessage}</Message>}

        {showResults && <div>
          <table className="s1_table list s1_flightsdeparturearrival">
            <tbody>
              {results.map(segment => (
                <FlightSearchResult
                  key={segment.id}
                  segment={segment}
                  onSegmentClicked={this.onSegmentClicked}
                  checked={selectedSegmentIds[segment.id]}
                />
              ))}
            </tbody>
          </table>
        </div>}
      </div>
      <div className="flex margin-top-large justify-end gap">
        <div>
          <button
            type="button"
            onClick={this.props.onManualEntryClicked}
            className="button-new secondary">
            {RESX.Webform.ManualInput}
          </button>
        </div>
        <div>
          <button
            type="button"
            className="button-new primary"
            value="Select"
            onClick={this.onCreateWithSelectedSegments}
            disabled={nothingSelected}
            name="Select">
            {RESX.Buttons.btnSelect}
          </button>
        </div>
      </div>
    </>;
  }
}
